import {
  Box,
  Button,
  DateRangePicker,
  ExpandableSection,
  Flashbar,
  FormField,
  Grid,
  Header,
  Link,
  Multiselect,
  Popover,
  Select,
  SpaceBetween,
  Toggle,
} from '@amzn/awsui-components-react';
import { cloneDeep, get } from 'lodash';
import moment from 'moment';
import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import { useResourceInfoQuery } from '../../pages/DomainDetails/hooks/useResourceInfoQuery';
import { accountMapping } from './constants';
import {
  addOptionToTop,
  esOsVersionOptions,
  i18nStrings,
  isValidRequestRange,
  isValidRequestRangeForOnDemand,
  isValidRequestRangeForDiagnosticDumps,
  logGroupOptions,
  relativeOptions,
  REQUEST_LOGS_INSTRUCTIONS,
  REQUEST_LOGS_INSTRUCTION_HEADER,
  TIMBER_COMMANDS_INFO,
  timezoneOptions,
} from './constants';
import { getRegionByPublicRegion } from '../../utils/appConfig';
import { isTimberRedirectSupported, buildTimberDownloadCommands, TimberCommandEntry } from './timberMechanicCommand';
import CopyText from '../../utils/copy-text';
import { useRequestLogsMutation } from './useRequestLogsMutation';
import { useRequestOnDemandDataMutation } from '../OnDemand/useRequestOnDemandData'
import {ListExecutions} from "../OnDemand/ListExecutions";
import {useGetDiagnosticDumps} from './useGetDiagnosticDumps';
import {useCheckDiagnosticDumpsExist} from './useCheckDiagnosticDumpsExist';
import { DiagnosticDumpsList } from '../DiagnosticDumps/DiagnosticDumpsList';
import { useContext } from 'react';
import { AppContext } from '../../utils/appContext';

const RequestLogs = (props) => {
  const params = useParams();
  const clientId = params['clientId'];
  const domainName = params['domainName'];

  const appContext = useContext(AppContext); // Add this
  const stage = appContext.stage;
  const region = appContext.region.publicRegion;
  const account = get(accountMapping, `${stage}.${region}`, []);

  const { data } = useResourceInfoQuery({
    domainIdentifier: clientId + ':' + domainName,
  });
  const esOsVersion: string = get(data, 'domain.esVersion', undefined);
  // Timber logs are owned by the domain's cell account.
  const timberAccount: string = get(data, 'domain.cellId');

  let defaultVersion = esOsVersionOptions[0]['options'][0];
  if (esOsVersion) {
    if (esOsVersion.includes('OS')) {
      defaultVersion = {
        label: esOsVersion.replace('OS_', ''),
        value: esOsVersion.replace('.', '_'),
      };
    } else {
      defaultVersion = {
        label: esOsVersion,
        value: 'ES_' + esOsVersion.replace('.', '_'),
      };
    }
  }
  var defaultVersionString = JSON.stringify(defaultVersion);

  useEffect(() => {
    setSelectedEsOsVersion(defaultVersion);
  }, [defaultVersionString]);

  const [selectedEsOsVersion, setSelectedEsOsVersion] =
      useState(defaultVersion);

  const [selectedLogGroups, setSelectedLogGroups] = useState([]);

  const [isOnDemandLogGroups, setIsOnDemandLogGroups] = useState(false)
  const [isDiagnosticDumpsGroup, setIsDiagnosticDumpsGroup] = useState(false);
  const [showDiagnosticDumps, setShowDiagnosticDumps] = useState(false);
  const [retrieveDiagnosticDumps, { loading: diagnosticDumpsLoading, data: diagnosticDumpsData, error: diagnosticDumpsError }] = useGetDiagnosticDumps();
  // Track diagnostic dumps availability and preserve time params for cross-view navigation
  const [checkDiagnosticDumpsExist, { data: checkDumpsData }] = useCheckDiagnosticDumpsExist();
  const [savedTimeParams, setSavedTimeParams] = useState<any>(null);
  const skipDiagnosticDumpsDefaultTimeRef = useRef(false);

  const [timberCommands, setTimberCommands] = useState<TimberCommandEntry[]>([]);

  const [useLegacyTimberFlow, setUseLegacyTimberFlow] = useState(false);

  // Show banner when diagnostic dumps are available for the requested time range
  useEffect(() => {
    if (checkDumpsData === undefined) return;
    if (checkDumpsData?.checkDiagnosticDumpsExist) {
      setFlashbarItems(prev => [
        ...prev.filter(item => item.id !== 'diagnostic_dumps_banner'),
        {
          type: 'info',
          header: 'Diagnostic Dumps Available',
          content: (
              <>
                Diagnostic dumps are available for the selected time period.{' '}
                <Button
                    variant="inline-link"
                    onClick={() => {
                      if (savedTimeParams) {
                        skipDiagnosticDumpsDefaultTimeRef.current = true;
                        setRequestDate(savedTimeParams.requestDate);
                        setHours(savedTimeParams.hours);
                        setIsHoursDisabled(savedTimeParams.requestDate.type === 'absolute');
                      }
                      setIsDiagnosticDumpsGroup(true);
                      setSelectedLogGroups([{ label: 'Node_Diagnostic_Dumps', value: 'node-diagnostic-dumps' }]);
                      setFlashbarItems(prev => prev.filter(item => item.id !== 'diagnostic_dumps_banner'));
                    }}
                >
                  View diagnostic dumps
                </Button>
              </>
          ),
          dismissible: true,
          dismissLabel: 'Dismiss message',
          onDismiss: () => setFlashbarItems(prev => prev.filter(item => item.id !== 'diagnostic_dumps_banner')),
          id: 'diagnostic_dumps_banner',
        }
      ]);
    }
  }, [checkDumpsData]);

  useEffect(() => {
    if (diagnosticDumpsData?.getDiagnosticDumps) {
      const commands = diagnosticDumpsData.getDiagnosticDumps;
      if (commands.length > 0) {
        setShowDiagnosticDumps(true);
        setFlashbarItems(prev => [...prev.filter(item => item.id !== 'diagnostic_dumps_success'), {
          type: 'success',
          content: `Successfully retrieved ${commands.length} diagnostic dump commands`,
          dismissible: true,
          dismissLabel: 'Dismiss message',
          onDismiss: () => setFlashbarItems(prev => prev.filter(item => item.id !== 'diagnostic_dumps_success')),
          id: 'diagnostic_dumps_success',
        }]);
      } else {
        setFlashbarItems(prev => [...prev.filter(item => item.id !== 'diagnostic_dumps_info'), {
          type: 'info',
          content: 'No diagnostic dumps found for this domain',
          dismissible: true,
          dismissLabel: 'Dismiss message',
          onDismiss: () => setFlashbarItems(prev => prev.filter(item => item.id !== 'diagnostic_dumps_info')),
          id: 'diagnostic_dumps_info',
        }]);
      }
    }
  }, [diagnosticDumpsData]);


  useEffect(() => {
    const isOnDemand = selectedLogGroups.some(logGroup => logGroup.value === 'ELB');
    if (isOnDemand){
      setIsOnDemandLogGroups(true);
      props.refreshData(true)
    } else {
      setIsOnDemandLogGroups(false);
      props.refreshData(false)
    }
  }, [selectedLogGroups]);


  const [hours, setHours] = useState('3');
  const [isHoursDisabled, setIsHoursDisabled] = useState(false);

  const [filteredLogGroupsOptions, setFilteredLogGroupsOptions] = useState([]);

  useEffect(() => {
    let logGroups = cloneDeep(logGroupOptions);
    for (let option of logGroups) {
      const label = option.label;
      const options = option.options;
      if (label === 'ElasticSearch' || label === 'Kibana') {
        let filteredOptions;
        if (label === 'ElasticSearch') {
          // e.g. selectedEsOsVersion.value = ES_7_10 or OS_1_0
          filteredOptions = options.filter((o) =>
              o.value.includes(selectedEsOsVersion.value + 'AMI') || o.value.includes('OpenSearch')
          );
          addOptionToTop(filteredOptions, 'auth.general.log');
          addOptionToTop(filteredOptions, 'elasticsearch.log');
        } else {
          let toBeFiltered;
          if (selectedEsOsVersion.value.includes('OS')) {
            toBeFiltered =
                selectedEsOsVersion.value.replace('OS', 'OSD') + 'AMI';
            // e.g. toBeFiltered OSD_1_0AMI
          } else {
            toBeFiltered =
                selectedEsOsVersion.value.replace('ES', 'Kibana') + 'AMI';
            // e.g. toBeFiltered Kibana_7_10AMI
          }
          filteredOptions = options.filter((o) =>
              o.value.includes(toBeFiltered)
          );
        }
        option.options = filteredOptions;
      }
    }
    logGroups = logGroups.filter((group) => group.options.length > 0);
    setFilteredLogGroupsOptions(logGroups);
  }, [selectedEsOsVersion]);

  // const [customLog, setCustomLog] = useState("");

  const [requestDate, setRequestDate] = useState<any>({
    type: 'relative',
    amount: 9,
    unit: 'hour',
  });

  useEffect(() => {
    if (isOnDemandLogGroups) {
      setRequestDate(prevState => ({
        ...prevState,
        amount: 6,
      }));
    } else if (isDiagnosticDumpsGroup && !skipDiagnosticDumpsDefaultTimeRef.current) {
      // Set default to 3 hours for diagnostic dumps
      setRequestDate({
        type: 'relative',
        amount: 3,
        unit: 'hour',
      });
    }
    skipDiagnosticDumpsDefaultTimeRef.current = false;
  }, [isOnDemandLogGroups, isDiagnosticDumpsGroup]);

  const [selectedRequestTimeZoneOption, setSelectedRequestTimeZoneOption] =
      useState({
        inputDisplay: 'UTC',
        label: 'UTC',
        value: 'UTC',
        code: 'UTC',
        offset: '0000',
        mins: 0,
      });

  const handleRequestTimezoneChange = (selectedOption) => {
    setSelectedRequestTimeZoneOption(selectedOption);
  };

  const handleRequestDateChange = ({ detail }) => {
    if ('relative' === detail.value.type) {
      setIsHoursDisabled(false);
    } else {
      setIsHoursDisabled(true);
    }
    setRequestDate(detail.value);
  };

  const [errorTextLogGroups, setErrorTextLogGroups] = useState('');
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')

  const validateRequest = () => {
    if (selectedLogGroups.length === 0) {
      setErrorTextLogGroups('Please select log group(s)');
    } else {
      handleRequestLogs();
      setErrorTextLogGroups('');
    }
  };

  const [requestLogs] = useRequestLogsMutation();
  const [requestOnDemandLogs] = useRequestOnDemandDataMutation();
  const [elbLogSubmit, setElbLogSubmit] = useState(false)
  const ListExecutionsRef = useRef();

  const handleRequestLogs = async () => {
    setTimberCommands([]);
    var startMoment, endMoment;

    if ('absolute' === requestDate.type) {
      startMoment = moment(requestDate.startDate);
      endMoment = moment(requestDate.endDate);
    } else if ('relative' === requestDate.type) {
      startMoment = moment().subtract(requestDate.amount, requestDate.unit);
      endMoment = moment();
    }

    const startTimeStamp = startMoment.utc().format();
    const endTimeStamp = endMoment.utc().format();
    setStartTime(startTimeStamp)
    setEndTime(endTimeStamp)

    let allStatus = [];
    if (isDiagnosticDumpsGroup) {
        console.log('Requesting diagnostic dumps...');
        
        // Prepare time parameters based on absolute or relative time selection
        const variables: any = {
          domainId: clientId + ':' + domainName,
          startTime: startTimeStamp,
        };
        
        if ('absolute' === requestDate.type) {
          variables.endTime = endTimeStamp;
        } else if ('relative' === requestDate.type) {
          variables.hours = hours;
        }
        
        retrieveDiagnosticDumps({ variables });
    }else if (isOnDemandLogGroups) {
      for (const logGroup of selectedLogGroups) {
        try{
          var resp;
          const formattedStartTimeStamp = moment.utc(startTimeStamp).format('YYYYMMDDTHHmm') + 'Z';
          const formattedEndTimeStamp = moment.utc(endTimeStamp).format('YYYYMMDDTHHmm') + 'Z';
          resp = await requestOnDemandLogs({
            variables: {
              domainId: clientId + ':' + domainName,
              eventType: logGroup.value,
              startTime: formattedStartTimeStamp,
              endTime: formattedEndTimeStamp,
              reBackfill: 'false'
            },
          });
          setElbLogSubmit(true)
          if (resp?.data?.requestOnDemandData?.success) {
            const status = resp?.data?.requestOnDemandData?.data;
            setFlashbarItems(prev => [...prev.filter(item => item.id !== 'on_demand_success'), {
              type: 'success',
              content: status,
              dismissible: true,
              dismissLabel: 'Dismiss message',
              onDismiss: () => setFlashbarItems(prev => prev.filter(item => item.id !== 'on_demand_success')),
              id: 'on_demand_success',
            }]);
          } else {
            const errorMessage = resp?.data?.requestOnDemandData?.error;
            setFlashbarItems(prev => [...prev.filter(item => item.id !== 'on_demand_error'), {
              type: 'error',
              content: `Error occured while requesting backfill. ${errorMessage}`,
              dismissible: true,
              dismissLabel: 'Dismiss message',
              onDismiss: () => setFlashbarItems(prev => prev.filter(item => item.id !== 'on_demand_error')),
              id: 'on_demand_error',
            }]);
          }
        } catch (e) {
          // TODO
        }
      }

      props.refreshData(true)
    } else {
      // Check for diagnostic dumps when requesting timber logs
      const checkVariables: any = {
        domainId: clientId + ':' + domainName,
        startTime: startTimeStamp,
      };

      if ('absolute' === requestDate.type) {
        checkVariables.endTime = endTimeStamp;
      } else if ('relative' === requestDate.type) {
        checkVariables.hours = hours;
      }

      if (useLegacyTimberFlow) {
        // Continue with timber log requests
        for (const logGroup of selectedLogGroups) {
          try {
            var resp;
            if ('absolute' === requestDate.type) {
              resp = await requestLogs({
                variables: {
                  domainId: clientId + ':' + domainName,
                  logGroup: logGroup.value,
                  startTime: startTimeStamp,
                  endTime: endTimeStamp,
                },
              });
            } else if ('relative' === requestDate.type) {
              resp = await requestLogs({
                variables: {
                  domainId: clientId + ':' + domainName,
                  logGroup: logGroup.value,
                  startTime: startTimeStamp,
                  hours: hours,
                },
              });
            }
            if (resp?.data?.requestLogs?.success) {
              const status = resp?.data?.requestLogs?.data;
                allStatus.push(...status);
            } else {
              const errorId = `loggroup_error_${logGroup.value}`;
              setFlashbarItems(prev => [...prev.filter(item => item.id !== errorId), {
                type: 'error',
                content: `Error occured while requesting the logs for loggroup ${logGroup}`,
                dismissible: true,
                dismissLabel: 'Dismiss message',
                onDismiss: () =>
                    setFlashbarItems(prev =>
                        prev.filter((item) => item.id !== errorId)
                    ),
                id: errorId,
              }]);
            }
          } catch (e) {
            // TODO
          }
        }
        var statusLogMapping = {};
        var actionMapping = {
          'new': [],
          'retry': [],
          'skipped': []
        };

        for (let statusRequest of allStatus) {
          let logFile = statusRequest.log;

          if (statusRequest.action) {
            actionMapping[statusRequest.action].push(logFile);
          }

          if (statusRequest.status === "skipped") {
            actionMapping.skipped.push(logFile);
            if (!(statusRequest.error in statusLogMapping)) {
              statusLogMapping[statusRequest.error] = [logFile];
            } else {
              statusLogMapping[statusRequest.error].push(logFile);
            }
          } else if ('status' in statusRequest) {
            let status = statusRequest.status;
            if (!(status in statusLogMapping)) {
              statusLogMapping[status] = [logFile];
            } else {
              statusLogMapping[status].push(logFile);
            }
          } else if ('error' in statusRequest) {
            let error = statusRequest.error;
            if (!(error in statusLogMapping)) {
              statusLogMapping[error] = [logFile];
            } else {
              statusLogMapping[error].push(logFile);
            }
          }
        }

        var flashbarItems = [];

        if (Object.keys(statusLogMapping).length === 1 && statusLogMapping['200'] && !actionMapping.skipped.length) {
          setFlashbarItems([{
            type: 'success',
            content: 'Logs are requested successfully',
            dismissible: true,
            dismissLabel: 'Dismiss message',
            onDismiss: () => setFlashbarItems([]),
          }]);
        } else {

          if (actionMapping.skipped.length > 0) {
            flashbarItems.push({
              type: 'warning',
              header: 'These log requests were skipped because some requests similar to them are still being processed',
              content: (
                  <div style={{whiteSpace: 'pre-line'}}>
                    {actionMapping.skipped.join('\n')}
                  </div>
              ),
              dismissible: true,
              dismissLabel: 'Dismiss message',
              onDismiss: () => setFlashbarItems((items) => items.filter(item => item.id !== 'skipped')),
              id: 'skipped',
            });
          }

          let message = [];
          for (var key of Object.keys(statusLogMapping)) {
            if (key !== '200' && !key.includes('Log request exists and is still being processed')) {
              message.push('* ' + key + ':\t' + statusLogMapping[key].join(', '));
            }
          }

          if (message.length > 0) {
            flashbarItems.push({
              type: 'info',
              header: 'Requests for these logs were not successful -',
              content: (
                  <div style={{whiteSpace: 'pre-line'}}>{message.join('\n')}</div>
              ),
              dismissible: true,
              dismissLabel: 'Dismiss message',
              onDismiss: () => setFlashbarItems((items) =>
                  items.filter((item) => item.id !== 'error')
              ),
              id: 'error',
            });
          }

          if ('200' in statusLogMapping) {
            flashbarItems.push({
              type: 'success',
              content: 'All other logs are requested successfully',
              dismissible: true,
              dismissLabel: 'Dismiss message',
              onDismiss: () => setFlashbarItems((items) =>
                  items.filter((item) => item.id !== 'success')
              ),
              id: 'success',
            });
          }
          setFlashbarItems(flashbarItems);
        }
      } else {
        // Copyable Mechanic CLI commands (Timber-Role) instead of the Tumbler log request
        const regionInfo = getRegionByPublicRegion(region);
        const showTimberError = (content: string) => setFlashbarItems([{
          type: 'error',
          content,
          dismissible: true,
          dismissLabel: 'Dismiss message',
          onDismiss: () => setFlashbarItems([]),
        }]);

        if (!regionInfo) {
          showTimberError(`Unable to resolve region info for ${region}.`);
          return;
        }
        const { partition, airportCode } = regionInfo;
        if (!isTimberRedirectSupported(partition)) {
          showTimberError('Timber log fetch via Mechanic is not available in this partition.');
          return;
        }
        if (!timberAccount) {
          showTimberError(`Unable to resolve the cell account for ${clientId}:${domainName}.`);
          return;
        }

        // Relative mode matches the legacy request: `hours` of logs from the start time (never in the future).
        const endIso = 'relative' === requestDate.type
          ? moment.min(moment.utc(startTimeStamp).add(Number(hours), 'hours'), moment.utc()).format()
          : endTimeStamp;

        setTimberCommands(buildTimberDownloadCommands({
          account: timberAccount,
          region,
          airportCode,
          partition,
          stage,
          selectedLogGroupValues: selectedLogGroups.map(g => g.value),
          clientId,
          domainName,
          startIso: startTimeStamp,
          endIso,
        }));

        setFlashbarItems([{
          type: 'info',
          content: TIMBER_COMMANDS_INFO,
          dismissible: true,
          dismissLabel: 'Dismiss message',
          onDismiss: () => setFlashbarItems([]),
        }]);
      }

      props.refreshData(false)

      // Save time parameters for potential diagnostic dumps view
      setSavedTimeParams({
        requestDate: requestDate,
        hours: hours,
        startTime: startTimeStamp,
        endTime: endTimeStamp,
      });

      // Fire dumps check AFTER log flashbar is set, so the useEffect appends to existing items
      checkDiagnosticDumpsExist({ variables: checkVariables });
    }
  };

  const [flashbarItems, setFlashbarItems] = useState([]);

  return (
      <>
        <Flashbar items={flashbarItems} />
        <ExpandableSection
            header={
              <Header
                  variant="h2"
                  info={
                    <div
                        style={{ display: 'inline-block' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                      <Popover
                          dismissAriaLabel="Close"
                          fixedWidth
                          header={REQUEST_LOGS_INSTRUCTION_HEADER}
                          position="right"
                          size="large"
                          triggerType="text"
                          renderWithPortal={true}
                          content={REQUEST_LOGS_INSTRUCTIONS.map((line) => (
                              <p>{line}</p>
                          ))}
                      >
                        <Link variant="info">Info</Link>
                      </Popover>
                    </div>
                  }
              >
                Request Logs
              </Header>
            }
            variant="container"
        >
          <SpaceBetween direction="vertical" size="l">
            <Grid
                gridDefinition={[
                  { colspan: { default: 12, xxs: 1 } },
                  { colspan: { default: 12, xxs: 3 } },
                  { colspan: { default: 12, xxs: 3 } },
                  { colspan: { default: 12, xxs: 2 } },
                  { colspan: { default: 12, xxs: 2 } },
                  { colspan: { default: 12, xxs: 1 } },
                ]}
            >
              <FormField label={<strong>ES/OS Version</strong>}>
                <Select
                    selectedOption={selectedEsOsVersion}
                    onChange={({ detail }) => {
                      setSelectedEsOsVersion(detail.selectedOption as any);
                      setSelectedLogGroups([]);
                    }}
                    options={esOsVersionOptions}
                    selectedAriaLabel="Selected"
                    placeholder="Choose options"
                />
              </FormField>
              <FormField
                  label={<strong>Select the logs to be retrieved</strong>}
                  errorText={errorTextLogGroups}
              >
                <Multiselect
                    selectedOptions={selectedLogGroups}
                    onChange={({ detail }) => {
                      const newSelectedOptions = detail.selectedOptions as any;
                      if (newSelectedOptions.some(group => group.value === 'ELB')) {
                        setSelectedLogGroups(newSelectedOptions.filter(group => group.value === 'ELB'));
                        setIsOnDemandLogGroups(true);
                        setIsDiagnosticDumpsGroup(false);
                      } else if (newSelectedOptions.some(group => group.value === 'node-diagnostic-dumps')) {
                        setSelectedLogGroups(newSelectedOptions.filter(group => group.value === 'node-diagnostic-dumps'));
                        setIsDiagnosticDumpsGroup(true);
                        setIsOnDemandLogGroups(false);
                      } else {
                        setSelectedLogGroups(newSelectedOptions);
                        setIsOnDemandLogGroups(false);
                        setIsDiagnosticDumpsGroup(false);
                      }
                    }}
                    options={filteredLogGroupsOptions}
                    selectedAriaLabel="Selected"
                    placeholder="Choose options"
                />
              </FormField>
              <FormField label={<strong>Date</strong>}>
                <DateRangePicker
                    onChange={handleRequestDateChange}
                    value={requestDate}
                    relativeOptions={relativeOptions}
                    i18nStrings={i18nStrings}
                    placeholder="Filter by a date and time range"
                    isValidRange={
                      isDiagnosticDumpsGroup 
                        ? isValidRequestRangeForDiagnosticDumps 
                        : isOnDemandLogGroups 
                          ? isValidRequestRangeForOnDemand 
                          : isValidRequestRange
                    }
                    showClearButton={false}
                    timeOffset={selectedRequestTimeZoneOption.mins}
                />
              </FormField>
              <FormField label={<strong>Time Zone</strong>}>
                <Select
                    selectedOption={selectedRequestTimeZoneOption}
                    onChange={({ detail }) =>
                        handleRequestTimezoneChange(detail.selectedOption)
                    }
                    options={timezoneOptions}
                    selectedAriaLabel="Selected"
                />
              </FormField>
              {!isHoursDisabled && (
                  <FormField label={<strong>Hours</strong>}>
                    <input
                        style={{ height: '32px' }}
                        type="number"
                        value={hours}
                        min={1}
                        max={isDiagnosticDumpsGroup ? 48 : isOnDemandLogGroups ? 48 : 72}
                        onChange={(event) => setHours(event.target.value)}
                    />
                  </FormField>
              )}

              <div style={{ marginTop: '26px' }}>
                <Button variant="normal" onClick={validateRequest}>
                  Submit
                </Button>
              </div>
            </Grid>

            <Toggle
              checked={useLegacyTimberFlow}
              onChange={({ detail }) => setUseLegacyTimberFlow(detail.checked)}
            >
              Use legacy Tumbler log request (instead of Mechanic CLI)
            </Toggle>

            {/* <FormField
            label={<strong>Custom Log Group</strong>}
          >
            <Autosuggest
              onChange={({ detail }) => setCustomLog(detail.value)}
              value={customLog}
              options={logGroupOptions}
              placeholder="Enter custom log group"
              enteredTextLabel={value  => `Use: "${value}"`}
              empty="No Matches Found"
              virtualScroll={true}
              disabled={true}
            />
          </FormField> */}

            {timberCommands.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <Box variant="h3">Run these commands in your terminal</Box>
                  <Box variant="small">
                    First time? Install the Mechanic CLI (<Link external href="https://w.amazon.com/bin/view/Mechanic/Service/CLI">setup guide</Link>) and run <code>mwinit -o</code> before running these.
                  </Box>
                  {timberCommands.map((entry, idx) => (
                      <div
                          key={entry.group}
                          style={{
                            marginTop: '16px',
                            paddingTop: '12px',
                            borderTop: idx === 0 ? 'none' : '1px solid #e9ebed',
                          }}
                      >
                        <Box variant="awsui-key-label">
                          {entry.logGroupValue} ({entry.variant})
                        </Box>
                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', margin: '8px 0' }}>
                          {entry.command}
                        </pre>
                        <CopyText
                            copyText={entry.command}
                            copyButtonLabel="Copy command"
                            successText="Command copied"
                            errorText="Failed to copy"
                        />
                      </div>
                  ))}
                </div>
            )}

            {elbLogSubmit && (
                <ListExecutions type='ELB' startTime={startTime} endTime={endTime} ref={ListExecutionsRef}/>
            )}

            {showDiagnosticDumps && diagnosticDumpsData?.getDiagnosticDumps && (
                <DiagnosticDumpsList
                    loading={diagnosticDumpsLoading}
                    data={diagnosticDumpsData.getDiagnosticDumps}
                    clientId={clientId}
                    domainName={domainName}
                    account={account}
                />
            )}
          </SpaceBetween>
        </ExpandableSection>
      </>
  );
};

export { RequestLogs };
